'use client';

import { getRepoAnalyzerData } from '@/actions/repoAnalyzer';
import { RepoAnalyzerResponse } from '@/types/repoAnalyzer';
import { atom, useAtom, useAtomValue } from 'jotai';
import { Check, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { RepoInfo } from './RepoInfo';

const repoInfoAtom = atom<RepoAnalyzerResponse | null>(null);

export function RepoAnalyzer() {
  const [repoInfo, setRepoInfo] = useAtom(repoInfoAtom);
  const [loading, setLoading] = useState(false);

  const methods = useForm<{
    url: string;
  }>({
    defaultValues: {
      url: '',
    },
  });

  const handleSubmit = async ({ url }: { url: string }) => {
    setLoading(true);
    try {
      const data = await getRepoAnalyzerData(url).catch(err => ({
        error: 'Network error occurred while fetching data',
        data: null
      }));
      
      if (data.error) {
        if (data.error === "Unauthorized") {
          methods.setError('url', {
            type: 'custom',
            message:
              'RepoAnalyzer is currently experiencing issues. Please report to the team.',
          });
        } else {
          methods.setError('url', {
            type: 'custom',
            message:
              'There was an error fetching the repo analysis. Please make sure the repo URL is valid.',
          });
        }
      } else if (data?.data) {
        setRepoInfo(data?.data);
      }
    } catch (err) {
      methods.setError('url', {
        type: 'custom',
        message:
          'There was an error fetching the repo analysis. Please make sure the repo URL is valid.',
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card className="flex flex-col justify-start">
      <CardHeader>
        <CardTitle>{'Github Analysis'}</CardTitle>
        {!repoInfo && (
          <CardDescription className="text-white">
            Is the Github Repo for this project legit? Validate it here.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex items-start sm:items-center justify-between flex-row gap-3 flex-wrap">
        {repoInfo ? (
          <RepoInfo {...repoInfo} />
        ) : (
          <Form {...methods}>
            <form
              onSubmit={methods.handleSubmit(handleSubmit)}
              className="flex flex-row gap-4 items-start justify-center w-full"
            >
              <FormField
                control={methods.control}
                name="url"
                rules={{
                  validate: (value: string) => {
                    const githubUrlPattern =
                      /^(https?:\/\/)?(www\.)?github\.com\/[a-zA-Z0-9_-]+(\/[a-zA-Z0-9_-]+)*$/;
                    return githubUrlPattern.test(value) || 'Invalid GitHub URL';
                  },
                }}
                render={({ field }) => (
                  <FormItem className="w-full flex-1">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter GitHub URL: https://github.com/username/repo"
                        className="bg-transparent border-purple-800 placeholder:text-gray-500 text-white"
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" loading={loading} dynamicLoader>
                Search
              </Button>
            </form>
          </Form>
        )}
        <div className="flex flex-col md:flex-row gap-6">
          <RepoGreenFlags />
          <RepoRedFlags />
        </div>
      </CardContent>
      <CardFooter className="w-full">
        {repoInfo && (
          <Button
            type="button"
            className="mr-4"
            variant={'outline'}
            onClick={() => {
              methods.reset();
              setRepoInfo(null);
            }}
          >
            Reset
          </Button>
        )}
        <p className="text-right text-muted-foreground text-sm ml-auto">
          powered by{' '}
          <a
            className="text-white hover:text-purple-400"
            href="https://repoanalyzer.io/"
            target="_blank"
          >
            RepoAnalyzer
          </a>
        </p>
      </CardFooter>
    </Card>
  );
}

const RepoGreenFlags = () => {
  const repoInfo = useAtomValue(repoInfoAtom);

  if (!repoInfo || repoInfo?.analysis?.greenFlags?.length < 1) return null;

  const {
    analysis: { greenFlags },
  } = repoInfo;

  return (
    <Card className="bg-green-200/5">
      <CardHeader>
        <CardTitle className="text-green-600">Green Flags</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {greenFlags.map((flag, index) => (
            <li key={index} className="text-sm flex items-start gap-3">
              <div className="h-5 w-5">
                <Check className="text-green-400" />
              </div>
              <p className="text-white">{flag}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const RepoRedFlags = () => {
  const repoInfo = useAtomValue(repoInfoAtom);

  if (!repoInfo || repoInfo?.analysis?.redFlags?.length < 1) return null;

  const {
    analysis: { redFlags },
  } = repoInfo;

  return (
    <Card className="bg-red-200/5">
      <CardHeader>
        <CardTitle className="text-red-600">Red Flags</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {redFlags.map((flag, index) => (
            <li key={index} className="text-sm flex items-start gap-3">
              <div className="h-5 w-5">
                <TriangleAlert className="text-red-400" />
              </div>
              <p className="text-white">{flag}</p>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
